import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { themeable } from '@instructure/ui-themeable';
import { uid } from '@instructure/uid';
import { omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'esvoZ',
  template: function template(theme) {
    return "\n\n.esvoZ_bGBk{fill:currentColor}\n\n.esvoZ_eXrk{display:inline-block}\n\n.esvoZ_cRbP{display:block}\n\n.esvoZ_drOs{color:inherit}\n\n.esvoZ_eCSh{color:".concat(theme.primaryColor || 'inherit', "}\n\n.esvoZ_buuG{color:").concat(theme.secondaryColor || 'inherit', "}\n\n.esvoZ_bFtJ{color:").concat(theme.primaryInverseColor || 'inherit', "}\n\n.esvoZ_dsSB{color:").concat(theme.secondaryInverseColor || 'inherit', "}\n\n.esvoZ_eZal{color:").concat(theme.successColor || 'inherit', "}\n\n.esvoZ_cVUo{color:").concat(theme.brandColor || 'inherit', "}\n\n.esvoZ_eScd{color:").concat(theme.warningColor || 'inherit', "}\n\n.esvoZ_cpQl{color:").concat(theme.errorColor || 'inherit', "}\n\n.esvoZ_cUGG{color:").concat(theme.alertColor || 'inherit', "}");
  },
  'root': 'esvoZ_bGBk',
  'inline': 'esvoZ_eXrk',
  'block': 'esvoZ_cRbP',
  'color--inherit': 'esvoZ_drOs',
  'color--primary': 'esvoZ_eCSh',
  'color--secondary': 'esvoZ_buuG',
  'color--primary-inverse': 'esvoZ_bFtJ',
  'color--secondary-inverse': 'esvoZ_dsSB',
  'color--success': 'esvoZ_eZal',
  'color--brand': 'esvoZ_cVUo',
  'color--warning': 'esvoZ_eScd',
  'color--error': 'esvoZ_cpQl',
  'color--alert': 'esvoZ_cUGG'
};
import theme from "./theme.js";
/**
---
category: components/utilities
---
**/

var InlineSVG = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(InlineSVG, _Component);

  var _super = _createSuper(InlineSVG);

  function InlineSVG() {
    var _this;

    _classCallCheck(this, InlineSVG);

    _this = _super.call(this);
    _this.titleId = uid('InlineSVG-title');
    _this.descId = uid('InlineSVG-desc');
    return _this;
  }

  _createClass(InlineSVG, [{
    key: "renderTitle",
    value: function renderTitle() {
      var title = this.props.title;
      return title ? /*#__PURE__*/React.createElement("title", {
        id: this.titleId
      }, title) : null;
    }
  }, {
    key: "renderDesc",
    value: function renderDesc(desc) {
      return desc ? /*#__PURE__*/React.createElement("desc", {
        id: this.descId
      }, desc) : null;
    }
  }, {
    key: "renderContent",
    value: function renderContent() {
      if (this.props.src) {
        var src = InlineSVG.prepareSrc(this.props.src);
        return /*#__PURE__*/React.createElement("g", {
          role: "presentation",
          dangerouslySetInnerHTML: {
            __html: src
          } // eslint-disable-line react/no-danger

        });
      } else {
        return /*#__PURE__*/React.createElement("g", {
          role: "presentation"
        }, this.props.children);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _classnames;

      var _this$props = this.props,
          style = _this$props.style,
          title = _this$props.title,
          description = _this$props.description,
          focusable = _this$props.focusable,
          children = _this$props.children,
          src = _this$props.src,
          color = _this$props.color,
          props = _objectWithoutProperties(_this$props, ["style", "title", "description", "focusable", "children", "src", "color"]); // if width or height are 'auto', don't supply anything to the SVG


      var width = this.props.width === 'auto' ? null : this.props.width;
      var height = this.props.height === 'auto' ? null : this.props.height;
      return /*#__PURE__*/React.createElement("svg", Object.assign({}, parseAttributes(this.props.src), omitProps(this.props, InlineSVG.propTypes, ['inline']), {
        style: _objectSpread({}, style, {
          width: width,
          height: height
        }),
        width: width,
        height: height,
        "aria-hidden": title ? null : 'true',
        "aria-labelledby": this.labelledBy,
        role: this.role,
        focusable: focusable ? 'true' : 'false',
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles["color--".concat(color)], color !== 'auto'), _defineProperty(_classnames, styles.inline, this.props.inline), _defineProperty(_classnames, styles.block, !this.props.inline), _defineProperty(_classnames, props.className, props.className), _classnames))
      }), this.renderTitle(), this.renderDesc(description), this.renderContent());
    }
  }, {
    key: "role",
    get: function get() {
      if (this.props.title) {
        return 'img';
      } else {
        return 'presentation';
      }
    }
  }, {
    key: "labelledBy",
    get: function get() {
      var ids = [];

      if (this.props.title) {
        ids.push(this.titleId);
      }

      if (this.props.description) {
        ids.push(this.descId);
      }

      return ids.length > 0 ? ids.join(' ') : null;
    }
  }]);

  InlineSVG.displayName = "InlineSVG";
  return InlineSVG;
}(Component), _class2.propTypes = {
  children: PropTypes.node,
  src: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  focusable: PropTypes.bool,

  /**
   * To let the SVG expand to fill its container, use `auto`
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * To let the SVG expand to fill its container, use `auto`
   */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inline: PropTypes.bool,
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'primary-inverse', 'secondary-inverse', 'success', 'error', 'alert', 'warning', 'brand', 'auto'])
}, _class2.defaultProps = {
  focusable: false,
  src: '',
  title: '',
  description: '',
  inline: true,
  children: null,
  width: '1em',
  height: '1em',
  color: 'inherit'
}, _class2.prepareSrc = function (src) {
  var pattern = /<svg[^>]*>((.|[\n\r])*)<\/svg>/;
  var matches = pattern.exec(src);
  return matches ? matches[1] : src;
}, _temp)) || _class) || _class);

function parseAttributes(src) {
  var attributes = {};
  var SVGAttributesRegExp = /<svg\s+([^>]*)\s*>/;
  var namesAndValuesRegExp = /(\S+)=["']?((?:.(?!["']?\s+(?:\S+)=|[>"']))+.)["']?/g;

  if (typeof src === 'string') {
    var attributesMatches = SVGAttributesRegExp.exec(src);
    var attributesString = attributesMatches ? attributesMatches[1] : '';
    var excludes = ['xmlns', 'xmlns:xlink', 'version'];
    var match = namesAndValuesRegExp.exec(attributesString);

    while (match != null) {
      if (excludes.indexOf(match[1]) === -1) {
        attributes[match[1]] = match[2] || (match[3] ? match[3] : match[4] ? match[4] : match[5]) || match[1]; // eslint-disable-line no-nested-ternary, max-len
      }

      match = namesAndValuesRegExp.exec(attributesString);
    }
  }

  return attributes;
}

export default InlineSVG;
export { InlineSVG };